<template>
  <div class="w-full p-4">
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 px-4 pt-3 pb-1">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">My Expenses</h2>
      <div class="flex items-center gap-2">
        <button
          v-if="bulkEditEligible && !bulkEditMode"
          class="px-3 py-1.5 text-sm rounded-md border border-dark-primary text-dark-primary hover:bg-dark-primary hover:text-white"
          @click="bulkEditMode = true"
        >Bulk Edit</button>
        <button
          class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary"
          @click="newExpense"
        >
          + New Expense
        </button>
      </div>
    </div>
    <!-- Filters -->
    <div class="flex flex-row flex-wrap gap-3 mb-3 items-end text-sm">
      <div class="flex-1 min-w-[14rem]">
        <label class="block text-xs text-gray-500 mb-0.5">Search</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search description, category, client, engagement..."
          class="w-full h-9 px-2 border border-gray-400 rounded-md"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Category</label>
        <select v-model="filters.category_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Paid by</label>
        <select v-model="filters.payment_method" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option value="personal_paid">Personal</option>
          <option value="company_paid">Company card</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Client reimbursable</label>
        <select v-model="filters.is_client_reimbursable" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Tags</span>
          <span v-if="filters.tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.tagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.tagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.tags" placeholder="Any" />
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Exclude tags</span>
          <span v-if="filters.exclude_tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.excludeTagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.excludeTagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.exclude_tags" placeholder="None" />
      </div>
      <div v-if="canViewIsRetro">
        <label class="block text-xs text-gray-500 mb-0.5">Retro</label>
        <select v-model="filters.is_retro" class="h-9 px-2 border border-gray-400 rounded-md w-32">
          <option :value="null">All</option>
          <option :value="true">Retro only</option>
          <option :value="false">Non-retro</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">From</label>
        <input v-model="filters.date_from" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">To</label>
        <input v-model="filters.date_to" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div v-if="activeTab === 'for_others'">
        <label class="block text-xs text-gray-500 mb-0.5">For (submitter)</label>
        <select v-model="filters.for_user_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All people</option>
          <option v-for="u in forOthersSubmitters" :key="u.id" :value="u.id">{{ submitterDisplayName(u) }}</option>
        </select>
      </div>
      <div v-if="activeTab === 'for_others'">
        <label class="block text-xs text-gray-500 mb-0.5">Status</label>
        <select v-model="filters.for_status" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option value="draft">Draft</option>
          <option value="submitted">Pending Manager</option>
          <option value="manager_approved">Pending Accounting</option>
          <option value="accounting_approved">Approved</option>
          <option value="closed">Closed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <button
        v-if="hasActiveFilters"
        class="px-2 py-1.5 text-xs border border-gray-400 rounded hover:bg-gray-100"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <!-- Status tabs -->
    <div class="flex flex-row flex-wrap gap-1 mb-2 text-sm border-b border-gray-300">
      <button
        v-for="tab in statusTabs"
        :key="tab.key"
        class="px-3 py-1.5 -mb-px border-b-2"
        :class="activeTab === tab.key
          ? 'border-accent-primary text-accent-primary font-semibold'
          : 'border-transparent text-gray-500 hover:text-dark-primary'"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
        <span class="ml-1 text-xs text-gray-400">{{ countForTab(tab.key) }}</span>
      </button>
    </div>

    <!-- Filtered total + (processed/for-others) grouping selector -->
    <div class="flex items-center mb-3 text-sm">
      <div v-if="supportsPayrollGrouping" class="flex items-center gap-2">
        <label class="text-xs text-gray-500">Grouping</label>
        <select v-model="payrollGrouping" class="h-9 px-2 border border-gray-400 rounded-md w-48 text-sm">
          <option value="ungrouped">Ungrouped</option>
          <option value="payroll">Grouped by Payroll Run</option>
        </select>
      </div>
      <div class="ml-auto text-gray-600">
        Filtered total
        <span class="font-semibold text-dark-primary">${{ formatTotal(filteredTotal) }}</span>
        <span class="text-gray-400 mx-1">·</span>
        {{ filteredExpenses.length }} row<span v-if="filteredExpenses.length !== 1">s</span>
        <template v-if="selectedIds.length">
          <span class="text-gray-400 mx-1">·</span>
          <span class="font-semibold text-accent-primary">{{ selectedIds.length }} selected</span>
        </template>
      </div>
    </div>

    <!-- Bulk action bar -->
    <div
      v-if="selectedIds.length > 0"
      class="flex flex-wrap items-center gap-2 mb-3 p-2 rounded bg-gray-100 border border-gray-300 text-sm"
    >
      <span>{{ selectedIds.length }} selected</span>
      <button
        v-if="allowBulkSubmit"
        class="px-2 py-1 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
        :disabled="submitting || submittableSelectedIds.length === 0"
        @click="submitSelected"
      >{{ submitting ? 'Submitting...' : submitButtonLabel }}</button>
      <button
        v-if="allowBulkRecall && recallableSelectedIds.length > 0"
        class="px-2 py-1 rounded border border-yellow-500 text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
        :disabled="recalling"
        @click="recallSelected"
      >{{ recalling ? 'Recalling...' : recallButtonLabel }}</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-gray-50"
        @click="openTagsModal('add')"
      >Add Tags</button>
      <button
        class="px-2 py-1 rounded border border-gray-400 hover:bg-gray-50"
        @click="openTagsModal('remove')"
      >Remove Tags</button>
      <button
        v-if="allowBulkDelete"
        class="px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 disabled:opacity-50"
        :disabled="deleting || deletableSelectedIds.length === 0"
        @click="deleteSelected"
      >{{ deleting ? 'Deleting...' : deleteButtonLabel }}</button>
      <button
        class="ml-auto text-xs text-gray-500 hover:underline"
        @click="selectedIds = []"
      >Clear</button>
    </div>
    </div>

    <!-- Mobile card list (visible on narrow screens only) -->
    <div v-if="!bulkEditMode" class="md:hidden space-y-2">
      <div v-if="loading" class="p-4 text-center text-gray-500 text-sm">Loading...</div>
      <div v-else-if="filteredExpenses.length === 0" class="p-4 text-center text-gray-500 text-sm">
        No expenses in this category yet.
      </div>
      <div
        v-for="exp in filteredExpenses"
        :key="`m-${exp.id}`"
        class="border border-gray-300 rounded-md bg-white p-3 cursor-pointer hover:bg-gray-50"
        @click="openExpense(exp.id)"
      >
        <div class="flex items-start gap-2">
          <div
            v-if="allowBulkSelect"
            class="pt-1"
            @click.stop
          >
            <input type="checkbox" :value="exp.id" v-model="selectedIds" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <div class="text-xs text-gray-500">
                  #{{ exp.id }} · {{ formatDate(exp.date_of_expense) }} · {{ exp.category ? exp.category.name : '—' }}
                </div>
                <div class="text-sm break-words mt-0.5">{{ exp.description }}</div>
                <div v-if="exp.tags && exp.tags.length" class="mt-1 flex flex-wrap gap-1">
                  <span
                    v-for="tag in exp.tags"
                    :key="tag.id"
                    @click.stop="addTagToFilter(tag)"
                    title="Click to filter by this tag"
                    class="inline-block px-1.5 py-0.5 text-xs rounded bg-accent-primary text-white cursor-pointer hover:bg-accent-secondary"
                  >{{ tag.name }}</span>
                </div>
                <div v-if="isProxy(exp)" class="text-xs text-blue-700 mt-0.5">
                  {{ proxyLabel(exp) }}
                </div>
              </div>
              <div class="text-right whitespace-nowrap flex-shrink-0">
                <div class="font-semibold">
                  <span
                    v-if="exp.payment_method === 'company_paid'"
                    class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                    title="Paid via company card"
                  >co.</span>
                  ${{ formatAmount(exp.amount) }}
                </div>
              </div>
            </div>
            <div class="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-600">
              <div class="flex flex-col items-start gap-0.5">
                <StatusPill :status="exp.status" />
                <span v-if="exp.is_retro" class="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
              </div>
              <span>{{ exp.receipt_count || 0 }} receipt<span v-if="exp.receipt_count !== 1">s</span></span>
              <span class="text-gray-400">Updated {{ formatDate(exp.updated_at) }}</span>
            </div>
            <div v-if="exp.is_client_reimbursable" class="mt-1 text-xs text-gray-700">
              {{ exp.client_name || '—' }}<span v-if="exp.engagement_title"> — {{ exp.engagement_title }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk edit grid -->
    <MyExpensesBulkEditTable
      v-if="bulkEditMode"
      :source="bulkEditSource"
      :for-others="activeTab === 'for_others'"
      @cancel="bulkEditMode = false"
      @saved="onBulkSaved"
    />

    <!-- Desktop / tablet table (hidden on phones) -->
    <div v-if="!bulkEditMode" class="hidden md:block border border-gray-400 rounded-md overflow-x-auto">
      <table class="table-fixed w-full min-w-[90rem] text-sm">
        <colgroup>
          <col class="w-10" />
          <col class="w-16" />
          <col class="w-24" />
          <col class="w-32" />
          <col />
          <col class="w-24" />
          <col class="w-36" />
          <col class="w-40" />
          <col class="w-28" />
          <col class="w-28" />
          <col class="w-20" />
          <col class="w-20" />
          <col class="w-20" />
          <col class="w-24" />
        </colgroup>
        <thead class="bg-dark-primary text-white">
          <tr>
            <th class="px-3 py-3">
              <input
                v-if="allowBulkSelect"
                type="checkbox"
                :checked="allVisibleSelected"
                @change="toggleAllVisible"
              />
            </th>
            <th class="text-left px-3 py-3">ID</th>
            <th class="text-left px-3 py-3">Date</th>
            <th class="text-left px-3 py-3">Category</th>
            <th class="text-left px-3 py-3">Description</th>
            <th class="text-right px-3 py-3">Amount</th>
            <th class="text-left px-3 py-3">Status</th>
            <th class="text-left px-3 py-3">Client / Engagement</th>
            <th class="text-left px-3 py-3">Reimbursed</th>
            <th class="text-left px-3 py-3">Invoiced</th>
            <th class="text-left px-3 py-3">Client Paid</th>
            <th class="text-left px-3 py-3">Bank Recon.</th>
            <th class="text-center px-3 py-3">Receipts</th>
            <th class="text-left px-3 py-3">Last Updated</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="14" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="filteredExpenses.length === 0">
            <td colspan="14" class="px-3 py-4 text-center text-gray-500">
              No expenses in this category yet.
            </td>
          </tr>
          <template v-for="row in displayRows" :key="row.kind === 'group' ? `g-${row.key}` : row.exp.id">
          <tr
            v-if="row.kind === 'group'"
            class="bg-amber-50 border-t border-amber-200 cursor-pointer hover:bg-amber-100"
            @click="toggleGroupCollapsed(row.key)"
          >
            <td colspan="14" class="px-3 py-2 text-sm">
              <span class="text-gray-500 mr-2 text-xs">{{ isGroupCollapsed(row.key) ? '▶' : '▼' }}</span>
              <span class="font-semibold text-dark-primary">{{ row.headerLabel }}</span>
              <span class="ml-3 text-xs text-gray-600">
                {{ row.count }} record<span v-if="row.count !== 1">s</span>,
                total ${{ formatAmount(row.total) }}
              </span>
            </td>
          </tr>
          <tr
            v-else-if="!row.groupKey || !isGroupCollapsed(row.groupKey)"
            :key="row.exp.id"
            class="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
            @click="openExpense(row.exp.id)"
          >
            <td class="px-3 py-3" @click.stop>
              <input
                v-if="allowBulkSelect"
                type="checkbox"
                :value="row.exp.id"
                v-model="selectedIds"
              />
            </td>
            <td class="px-3 py-3 whitespace-nowrap text-gray-500">#{{ row.exp.id }}</td>
            <td class="px-3 py-3 whitespace-nowrap">{{ formatDate(row.exp.date_of_expense) }}</td>
            <td class="px-3 py-3 break-words">{{ row.exp.category ? row.exp.category.name : '—' }}</td>
            <td class="px-3 py-3 break-words">
              {{ row.exp.description }}
              <div v-if="row.exp.tags && row.exp.tags.length" class="mt-1 flex flex-wrap gap-1">
                <span
                  v-for="tag in row.exp.tags"
                  :key="tag.id"
                  @click.stop="addTagToFilter(tag)"
                  title="Click to filter by this tag"
                  class="inline-block px-1.5 py-0.5 text-xs rounded bg-accent-primary text-white cursor-pointer hover:bg-accent-secondary"
                >{{ tag.name }}</span>
              </div>
              <div
                v-if="isProxy(row.exp)"
                class="text-xs text-blue-700 mt-0.5"
              >{{ proxyLabel(row.exp) }}</div>
            </td>
            <td class="px-3 py-3 text-right whitespace-nowrap">
              <span
                v-if="row.exp.payment_method === 'company_paid'"
                class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                title="Paid via company card"
              >co.</span>
              ${{ formatAmount(row.exp.amount) }}
            </td>
            <td class="px-3 py-3">
              <StatusPill :status="row.exp.status" />
              <div v-if="row.exp.is_retro" class="mt-1">
                <span class="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
              </div>
            </td>
            <td class="px-3 py-3">
              <template v-if="row.exp.is_client_reimbursable">
                <span>{{ row.exp.client_name || '—' }}</span>
                <span v-if="row.exp.engagement_title"> — {{ row.exp.engagement_title }}</span>
              </template>
              <span v-else class="text-gray-400">not client reimbursable</span>
            </td>
            <td class="px-3 py-3">
              <span v-if="row.exp.payment_method !== 'personal_paid'" class="text-gray-300">—</span>
              <template v-else-if="row.exp.submitter_reimbursed">
                <span class="text-green-700">Yes</span>
                <span v-if="row.exp.payroll_batch_reference" class="block text-xs text-gray-500 break-words">{{ row.exp.payroll_batch_reference }}</span>
              </template>
              <span v-else class="text-gray-400">No</span>
            </td>
            <td class="px-3 py-3">
              <span v-if="!row.exp.is_client_reimbursable" class="text-gray-300">—</span>
              <template v-else-if="row.exp.client_invoiced">
                <span class="text-green-700">Yes</span>
                <span v-if="row.exp.client_invoice_batch_reference" class="block text-xs text-gray-500 break-words">{{ row.exp.client_invoice_batch_reference }}</span>
              </template>
              <span v-else class="text-gray-400">No</span>
            </td>
            <td class="px-3 py-3 whitespace-nowrap">
              <span v-if="!row.exp.is_client_reimbursable" class="text-gray-300">—</span>
              <span v-else-if="row.exp.client_paid" class="text-green-700">Yes</span>
              <span v-else class="text-gray-400">No</span>
            </td>
            <td class="px-3 py-3 whitespace-nowrap">
              <span v-if="row.exp.payment_method !== 'company_paid'" class="text-gray-300">—</span>
              <span v-else-if="row.exp.bank_reconciled" class="text-green-700">Yes</span>
              <span v-else class="text-gray-400">No</span>
            </td>
            <td class="px-3 py-3 text-center">{{ row.exp.receipt_count || 0 }}</td>
            <td class="px-3 py-3 whitespace-nowrap">{{ formatDate(row.exp.updated_at) }}</td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div v-if="bulkErrors.length > 0" class="mt-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700 whitespace-pre-wrap">
      Some records could not be processed:
      <ul class="list-disc ml-5 mt-1">
        <li v-for="e in bulkErrors" :key="e.id">#{{ e.id }}: {{ e.detail }}</li>
      </ul>
    </div>

    <TagsBulkModal
      v-if="tagsModalOpen"
      :recordIds="selectedIds"
      :mode="tagsModalMode"
      @close="tagsModalOpen = false"
      @applied="onTagsApplied"
    />
  </div>
</template>

<script>
export default {
  name: "my-expenses",
};
</script>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getExpenses,
  bulkSubmitExpenses,
  bulkRecallExpenses,
  deleteExpense,
  getExpenseGroupLabels,
} from "@/services/Expense.js";
import { getExpenseCategories } from "@/services/ExpenseCategory.js";
import StatusPill from "./StatusPill.vue";
import TagsBulkModal from "./TagsBulkModal.vue";
import TagPicker from "./TagPicker.vue";
import MyExpensesBulkEditTable from "./MyExpensesBulkEditTable.vue";
import { useStickyOffset } from "@/composables/useStickyOffset";

const router = useRouter();
const route = useRoute();
const store = useStore();
const $toast = useToast();

const canViewIsRetro = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isDev || p.isSuperuser || p.isExpenseAdmin || p.isAccountingGroup);
});

const loading = ref(false);
const submitting = ref(false);
const expenses = ref([]);
const forOthersExpenses = ref([]);
const categories = ref([]);
const groupLabelOptions = ref([]);
// Restore the active tab from the URL so navigating back from the
// expense form lands on whichever tab the user was on. statusTabs is
// defined below; the keys are the source of truth — keep them in sync.
const VALID_TAB_KEYS = new Set(["drafts", "pending", "rejected", "processed", "for_others"]);
const initialTab = VALID_TAB_KEYS.has(route.query.tab) ? route.query.tab : "drafts";
const activeTab = ref(initialTab);
// Grouping mode for the Processed tab. 'ungrouped' is the default;
// 'payroll' groups records by their payroll_batch_reference so a
// submitter can see "this is what got reimbursed in run X".
const payrollGrouping = ref('ungrouped');
// Tabs that expose the "Grouped by Payroll Run" selector. Both
// Processed and For Others can show records that have been pulled
// into a payroll batch, so grouping by run is useful in either.
const supportsPayrollGrouping = computed(() =>
  activeTab.value === 'processed' || activeTab.value === 'for_others'
);
const selectedIds = ref([]);
const topStickyRef = ref(null);
const topStickyHeight = useStickyOffset(topStickyRef);
const bulkErrors = ref([]);

const tagsModalOpen = ref(false);
const tagsModalMode = ref("add");

const filters = reactive({
  search: "",
  category_id: null,
  payment_method: null,
  is_client_reimbursable: null,
  group_label: "",
  tags: [],
  tagCombinator: "or",
  exclude_tags: [],
  excludeTagCombinator: "or",
  is_retro: null,
  date_from: null,
  date_to: null,
  for_user_id: null,
  for_status: 'draft',
});

const forOthersSubmitters = computed(() => {
  const map = new Map();
  for (const e of forOthersExpenses.value) {
    if (e.submitter && !map.has(e.submitter.id)) {
      map.set(e.submitter.id, e.submitter);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const an = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username || '').toLowerCase();
    const bn = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.username || '').toLowerCase();
    return an.localeCompare(bn);
  });
});

function submitterDisplayName(u) {
  if (!u) return '';
  const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return n || u.username || '';
}

const statusTabs = [
  { key: "drafts", label: "Drafts", statuses: ["draft"] },
  { key: "pending", label: "Pending Review", statuses: ["submitted", "manager_approved"] },
  { key: "rejected", label: "Needs Revision", statuses: ["rejected"] },
  { key: "processed", label: "Processed", statuses: ["accounting_approved", "closed"] },
  { key: "for_others", label: "For Others", statuses: null, forOthers: true },
];

function expenseMatchesFilters(e) {
  if (!e) return false;
  if (filters.search) {
    const q = filters.search.trim().toLowerCase();
    if (q) {
      const submitterText = e.submitter
        ? `${e.submitter.first_name || ''} ${e.submitter.last_name || ''} ${e.submitter.username || ''}`
        : "";
      const haystack = [
        e.description,
        e.category ? e.category.name : "",
        e.client_name,
        e.engagement_title,
        submitterText,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(q)) return false;
    }
  }
  if (filters.category_id && (!e.category || e.category.id !== filters.category_id)) return false;
  if (filters.payment_method && e.payment_method !== filters.payment_method) return false;
  if (filters.is_client_reimbursable !== null && !!e.is_client_reimbursable !== filters.is_client_reimbursable) return false;
  if (filters.tags && filters.tags.length) {
    const wantedIds = new Set(filters.tags.map((t) => t.id).filter((x) => x != null));
    const wantedNames = new Set(filters.tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const has = (tag) => wantedIds.has(tag.id) || wantedNames.has((tag.name || '').toLowerCase());
    const expTags = e.tags || [];
    if (filters.tagCombinator === 'and') {
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const allMatch = filters.tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (!allMatch) return false;
    } else {
      if (!expTags.some(has)) return false;
    }
  }
  if (filters.exclude_tags && filters.exclude_tags.length) {
    const excludeIds = new Set(filters.exclude_tags.map((t) => t.id).filter((x) => x != null));
    const excludeNames = new Set(filters.exclude_tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const expTags = e.tags || [];
    const has = (tag) => excludeIds.has(tag.id) || excludeNames.has((tag.name || '').toLowerCase());
    if (filters.excludeTagCombinator === 'and') {
      // Hide only rows that carry ALL of the excluded tags.
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const hasAll = filters.exclude_tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (hasAll) return false;
    } else if (expTags.some(has)) {
      return false;
    }
  }
  if (filters.is_retro !== null && !!e.is_retro !== filters.is_retro) return false;
  if (filters.date_from && (!e.date_of_expense || e.date_of_expense < filters.date_from)) return false;
  if (filters.date_to && (!e.date_of_expense || e.date_of_expense > filters.date_to)) return false;
  // For Others tab — additional filters that only make sense there.
  if (activeTab.value === 'for_others') {
    if (filters.for_user_id && (!e.submitter || e.submitter.id !== filters.for_user_id)) return false;
    if (filters.for_status && e.status !== filters.for_status) return false;
  }
  return true;
}

function baseSourceForTab(key) {
  const tab = statusTabs.find((t) => t.key === key);
  if (!tab) return [];
  if (tab.forOthers) return forOthersExpenses.value;
  return expenses.value.filter((e) => tab.statuses.includes(e.status));
}

const hasActiveFilters = computed(() => {
  return !!(
    filters.search ||
    filters.category_id ||
    filters.payment_method ||
    filters.is_client_reimbursable !== null ||
    filters.group_label ||
    (filters.tags && filters.tags.length) ||
    (filters.exclude_tags && filters.exclude_tags.length) ||
    filters.is_retro !== null ||
    filters.date_from ||
    filters.date_to ||
    (activeTab.value === 'for_others' && (filters.for_user_id || filters.for_status))
  );
});

// Decorate the filtered list with group-header rows when the user
// has chosen a grouping on the Processed tab. Other tabs always emit
// flat expense rows.
const displayRows = computed(() => {
  if (supportsPayrollGrouping.value && payrollGrouping.value === 'payroll') {
    const list = [...filteredExpenses.value].sort((a, b) => {
      const ar = a.payroll_batch_reference;
      const br = b.payroll_batch_reference;
      if (ar !== br) {
        if (!ar) return 1;
        if (!br) return -1;
        return ar < br ? -1 : 1;
      }
      return (a.date_of_expense || '') > (b.date_of_expense || '') ? -1 : 1;
    });
    const rows = [];
    let lastKey = Symbol('init');
    let runningGroup = null;
    for (const exp of list) {
      const key = exp.payroll_batch_reference || '__none__';
      if (key !== lastKey) {
        runningGroup = {
          kind: 'group',
          key,
          headerLabel: exp.payroll_batch_reference
            ? `Payroll Run ${exp.payroll_batch_reference}`
            : '(not yet reimbursed)',
          count: 0,
          total: 0,
        };
        rows.push(runningGroup);
        lastKey = key;
      }
      runningGroup.count += 1;
      runningGroup.total += parseFloat(exp.amount) || 0;
      rows.push({ kind: 'expense', exp, groupKey: key });
    }
    return rows;
  }
  return filteredExpenses.value.map((exp) => ({ kind: 'expense', exp }));
});

// Track which Processed-tab group sections the user has collapsed.
const collapsedGroupKeys = ref(new Set());

function toggleGroupCollapsed(key) {
  const next = new Set(collapsedGroupKeys.value);
  if (next.has(key)) next.delete(key); else next.add(key);
  collapsedGroupKeys.value = next;
}

function isGroupCollapsed(key) {
  return collapsedGroupKeys.value.has(key);
}

const filteredExpenses = computed(() => {
  return baseSourceForTab(activeTab.value).filter(expenseMatchesFilters);
});

const filteredTotal = computed(() =>
  filteredExpenses.value.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
);

function formatTotal(n) {
  return (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function countForTab(key) {
  return baseSourceForTab(key).filter(expenseMatchesFilters).length;
}

function addTagToFilter(tag) {
  if (!tag) return;
  const idMatch = tag.id != null && filters.tags.some((t) => t.id === tag.id);
  const nameMatch = filters.tags.some((t) => (t.name || '').toLowerCase() === (tag.name || '').toLowerCase());
  if (idMatch || nameMatch) return;
  filters.tags.push({ id: tag.id, name: tag.name });
}

function clearFilters() {
  filters.search = "";
  filters.category_id = null;
  filters.payment_method = null;
  filters.is_client_reimbursable = null;
  filters.group_label = "";
  filters.tags = [];
  filters.tagCombinator = "or";
  filters.exclude_tags = [];
  filters.excludeTagCombinator = "or";
  filters.is_retro = null;
  filters.date_from = null;
  filters.date_to = null;
  filters.for_user_id = null;
  filters.for_status = null;
}

// Bulk row selection is always available so the user can apply (or clear)
// a group label across an arbitrary set of records. The Submit Selected
// action is gated to tabs that can actually contain submittable rows.
const allowBulkSelect = computed(() => true);
const allowBulkSubmit = computed(() =>
  activeTab.value === 'drafts'
  || activeTab.value === 'rejected'
  || activeTab.value === 'for_others'
);

// Tabs that mix states need per-state filtering before bulk actions
// so the user doesn't get spurious per-record errors for rows in the
// wrong state for the requested action.
function _idToStatusMap(list) {
  return new Map(list.map((e) => [e.id, e.status]));
}

const submittableSelectedIds = computed(() => {
  const submittable = new Set(['draft', 'rejected']);
  if (activeTab.value !== 'for_others') return selectedIds.value.slice();
  const m = _idToStatusMap(forOthersExpenses.value);
  return selectedIds.value.filter((id) => submittable.has(m.get(id)));
});

// A record is recallable when status='submitted' (manager hasn't acted)
// or when status='manager_approved' but it auto-advanced because the
// submitter has no reporting manager — manager is null in that case
// and the submitter / delegate should still be able to pull it back
// as long as accounting hasn't acted.
function _isRecallable(e) {
  if (!e) return false;
  if (e.status === 'submitted') return true;
  if (e.status === 'manager_approved' && !e.manager) return true;
  return false;
}

const recallableSelectedIds = computed(() => {
  const list = activeTab.value === 'for_others'
    ? forOthersExpenses.value
    : expenses.value;
  const byId = new Map(list.map((e) => [e.id, e]));
  return selectedIds.value.filter((id) => _isRecallable(byId.get(id)));
});

const allowBulkRecall = computed(() =>
  activeTab.value === 'pending' || activeTab.value === 'for_others'
);

// Delete is only safe for Drafts. The For Others tab is a mixed bag,
// so we filter the selection down to the draft rows on click; the
// button stays disabled if no drafts are selected.
const allowBulkDelete = computed(() =>
  activeTab.value === 'drafts' || activeTab.value === 'for_others'
);

const deletableSelectedIds = computed(() => {
  const list = activeTab.value === 'for_others'
    ? forOthersExpenses.value
    : expenses.value;
  const m = _idToStatusMap(list);
  return selectedIds.value.filter((id) => m.get(id) === 'draft');
});

const deleteButtonLabel = computed(() => {
  const n = deletableSelectedIds.value.length;
  if (activeTab.value === 'for_others' && n !== selectedIds.value.length) {
    return `Delete ${n} draft${n === 1 ? '' : 's'}`;
  }
  return n === selectedIds.value.length && n > 0
    ? `Delete ${n} draft${n === 1 ? '' : 's'}`
    : 'Delete Selected';
});

const submitButtonLabel = computed(() => {
  const n = submittableSelectedIds.value.length;
  if (activeTab.value === 'for_others' && n !== selectedIds.value.length) {
    return `Submit ${n} draft${n === 1 ? '' : 's'}`;
  }
  return 'Submit Selected';
});

const recallButtonLabel = computed(() => {
  const n = recallableSelectedIds.value.length;
  return n !== selectedIds.value.length
    ? `Recall ${n} submission${n === 1 ? '' : 's'}`
    : 'Recall Selected';
});

const allVisibleSelected = computed(() =>
  filteredExpenses.value.length > 0 &&
  filteredExpenses.value.every((e) => selectedIds.value.includes(e.id))
);

function toggleAllVisible() {
  if (allVisibleSelected.value) {
    const visibleIds = new Set(filteredExpenses.value.map((e) => e.id));
    selectedIds.value = selectedIds.value.filter((id) => !visibleIds.has(id));
  } else {
    const toAdd = filteredExpenses.value.map((e) => e.id);
    selectedIds.value = Array.from(new Set([...selectedIds.value, ...toAdd]));
  }
}

async function submitSelected() {
  const ids = submittableSelectedIds.value;
  if (ids.length === 0) return;
  submitting.value = true;
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkSubmitExpenses(token, [...ids]);
    const applied = (result.applied_ids || []).length;
    const errs = result.errors || [];
    bulkErrors.value = errs;
    if (applied > 0) {
      $toast.open({
        message: `Submitted ${applied} record${applied === 1 ? '' : 's'}${errs.length ? ' — see errors below' : ''}`,
        type: errs.length ? 'warning' : 'success',
        duration: 3000,
      });
    } else if (errs.length > 0) {
      $toast.open({ message: 'No records submitted', type: 'error', duration: 3000 });
    }
    selectedIds.value = [];
    await loadExpenses();
  } catch (error) {
    $toast.open({ message: 'Bulk submit failed', type: 'error', duration: 3000 });
  }
  submitting.value = false;
}

const recalling = ref(false);

async function recallSelected() {
  const ids = recallableSelectedIds.value;
  if (ids.length === 0) return;
  if (!window.confirm(`Recall ${ids.length} submission${ids.length === 1 ? '' : 's'}? They will return to Draft so they can be edited.`)) return;
  recalling.value = true;
  bulkErrors.value = [];
  const token = store.state.auth.token;
  try {
    const result = await bulkRecallExpenses(token, [...ids]);
    const applied = (result.applied_ids || []).length;
    const errs = result.errors || [];
    bulkErrors.value = errs;
    if (applied > 0) {
      $toast.open({
        message: `Recalled ${applied} record${applied === 1 ? '' : 's'}${errs.length ? ' — see errors below' : ''}`,
        type: errs.length ? 'warning' : 'success',
        duration: 3000,
      });
    } else if (errs.length > 0) {
      $toast.open({ message: 'No records recalled', type: 'error', duration: 3000 });
    }
    selectedIds.value = [];
    await loadExpenses();
  } catch (error) {
    $toast.open({ message: 'Bulk recall failed', type: 'error', duration: 3000 });
  }
  recalling.value = false;
}

const deleting = ref(false);

async function deleteSelected() {
  const ids = deletableSelectedIds.value;
  if (ids.length === 0) return;
  if (!window.confirm(
    `Delete ${ids.length} draft${ids.length === 1 ? '' : 's'}? This cannot be undone.`
  )) return;
  deleting.value = true;
  bulkErrors.value = [];
  const token = store.state.auth.token;
  // No bulk-delete endpoint — fan out per record and collect failures.
  const errs = [];
  let applied = 0;
  for (const id of ids) {
    try {
      await deleteExpense(token, id);
      applied += 1;
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Delete failed.';
      errs.push({ id, detail });
    }
  }
  bulkErrors.value = errs;
  if (applied > 0) {
    $toast.open({
      message: `Deleted ${applied} record${applied === 1 ? '' : 's'}${errs.length ? ' — see errors below' : ''}`,
      type: errs.length ? 'warning' : 'success',
      duration: 3000,
    });
  } else if (errs.length > 0) {
    $toast.open({ message: 'No records deleted', type: 'error', duration: 3000 });
  }
  selectedIds.value = [];
  await loadExpenses();
  deleting.value = false;
}

function formatDate(iso) {
  if (!iso) return "—";
  // Date-only strings (YYYY-MM-DD) parse as UTC midnight, which shifts
  // back a day in negative offsets — interpret them as local instead.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function isProxy(exp) {
  return exp && exp.created_by && exp.submitter && exp.created_by.id !== exp.submitter.id;
}

function submitterLabel(exp) {
  if (!exp || !exp.submitter) return '';
  const s = exp.submitter;
  const name = `${s.first_name || ''} ${s.last_name || ''}`.trim();
  return name || s.username;
}

function userFullName(u) {
  if (!u) return '';
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || '';
}

// "Anastasia Camellias for Scott Shedd" — used on delegated rows so
// it's clear at a glance who entered the record AND who it belongs to.
function proxyLabel(exp) {
  if (!exp || !exp.submitter || !exp.created_by) return '';
  return `${userFullName(exp.created_by)} for ${userFullName(exp.submitter)}`;
}

function formatAmount(amount) {
  if (amount === null || amount === undefined) return "0.00";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return num.toFixed(2);
}

async function loadExpenses() {
  loading.value = true;
  const token = store.state.auth.token;
  const userId = store.state.auth.user ? store.state.auth.user.id : null;
  const ownQp = userId ? `?submitter=${userId}&page_size=250` : "?page_size=250";
  try {
    const [ownData, othersData] = await Promise.all([
      getExpenses(token, ownQp),
      userId
        ? getExpenses(token, `?created_by=${userId}&created_for_others=true&page_size=250`)
        : Promise.resolve([]),
    ]);
    expenses.value = Array.isArray(ownData) ? ownData : (ownData.results || []);
    forOthersExpenses.value = Array.isArray(othersData) ? othersData : (othersData.results || []);
  } catch (error) {
    console.log(error);
    $toast.open({ message: "Failed to load expenses", type: "error", duration: 3000 });
  }
  loading.value = false;
}

async function loadCategories() {
  const token = store.state.auth.token;
  try {
    const data = await getExpenseCategories(token);
    categories.value = Array.isArray(data) ? data : (data.results || []);
  } catch (error) {
    // ignore — the picker just stays empty
  }
}

async function loadGroupLabels() {
  const token = store.state.auth.token;
  try {
    const data = await getExpenseGroupLabels(token);
    groupLabelOptions.value = (data && data.group_labels) || [];
  } catch (error) {
    groupLabelOptions.value = [];
  }
}

function openTagsModal(mode) {
  if (selectedIds.value.length === 0) return;
  tagsModalMode.value = mode || "add";
  tagsModalOpen.value = true;
}

async function onTagsApplied() {
  tagsModalOpen.value = false;
  selectedIds.value = [];
  await loadExpenses();
}

function newExpense() {
  router.push({ name: "expense-new" });
}

function openExpense(id) {
  router.push({ name: "expense-detail", params: { id } });
}

const bulkEditMode = ref(false);
const bulkEditEligible = computed(() =>
  activeTab.value === 'drafts' || activeTab.value === 'for_others'
);

// In Drafts mode the user only sees their own draft rows; pass them
// through filters so the grid honours search/category/etc. In For Others
// mode show every filtered row but the grid will lock non-draft rows.
const bulkEditSource = computed(() => filteredExpenses.value);

async function onBulkSaved() {
  await loadExpenses();
  bulkEditMode.value = false;
}

watch(activeTab, (newTab) => {
  selectedIds.value = [];
  bulkErrors.value = [];
  bulkEditMode.value = false;
  if (route.query.tab !== newTab) {
    router.replace({ query: { ...route.query, tab: newTab } });
  }
});

onMounted(() => {
  loadCategories();
  loadGroupLabels();
  loadExpenses();
});
</script>
